'use client';

import { Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { Eyebrow } from '~/(tenant)/_components/native/_primitives';

import type { MembershipPlanPerk } from '../_lib/server/available-memberships.loader';

/**
 * Read-only "Included perks" block for a membership plan card. Lists the
 * packages auto-granted when the member activates the plan — each with its
 * session count and validity window. These come free with the plan, so no
 * price is shown; an "Included" badge makes that explicit.
 *
 * Renders nothing when the plan has no perks (no empty block).
 *
 * Visually mirrors the benefits list in NativePurchaseMembershipCard so the
 * two sections read as one editorial unit.
 */
export function MembershipPlanPerks({
  perks,
  accent,
}: {
  perks: MembershipPlanPerk[];
  /** Plan accent color, reused from the host card. */
  accent: string;
}) {
  const t = useNativeTokens();
  const { t: translate } = useTranslation();

  if (perks.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: 18 }} data-test="membership-plan-perks">
      <Eyebrow size="sm" style={{ marginBottom: 10 }}>
        <Trans
          i18nKey="common:memberClub.memberships.purchase.includedPerksHeading"
          defaults="Included perks"
        />
      </Eyebrow>

      {perks.map((perk) => {
        const sessions = translate(
          'common:memberClub.memberships.purchase.perkSessions',
          {
            count: perk.booking_count,
            defaultValue_one: '{{count}} session',
            defaultValue_other: '{{count}} sessions',
          },
        );

        const validity =
          perk.validity_days != null
            ? translate(
                'common:memberClub.memberships.purchase.perkValidity',
                {
                  count: perk.validity_days,
                  defaultValue_one: 'valid {{count}} day',
                  defaultValue_other: 'valid {{count}} days',
                },
              )
            : null;

        const meta = [sessions, validity].filter(Boolean).join(' · ');

        return (
          <div
            key={perk.id}
            data-test="membership-plan-perk"
            data-perk-id={perk.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '8px 0',
              borderTop: `1px solid ${t.line}`,
            }}
          >
            <Gift
              size={15}
              strokeWidth={1.6}
              aria-hidden
              style={{ color: accent, marginTop: 2, flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                dir="auto"
                style={{
                  fontFamily: t.fontSans,
                  fontSize: 13,
                  fontWeight: 500,
                  color: t.foreground,
                  lineHeight: 1.4,
                }}
              >
                {perk.name}
              </div>
              {meta ? (
                <div
                  style={{
                    fontFamily: t.fontMono,
                    fontSize: 10,
                    letterSpacing: '0.04em',
                    color: t.muted,
                    marginTop: 3,
                  }}
                >
                  {meta}
                </div>
              ) : null}
            </div>
            <span
              style={{
                padding: '3px 8px',
                background: `${accent}14`,
                color: accent,
                fontFamily: t.fontMono,
                fontSize: 8,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                flexShrink: 0,
                alignSelf: 'center',
              }}
              data-test="membership-plan-perk-included-badge"
            >
              <Trans
                i18nKey="common:memberClub.memberships.purchase.perkIncluded"
                defaults="Included"
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}
